import Image from 'next/image';
import React, { useEffect, useState } from 'react';
import { toast } from '~/utils/toast';
import Button from '~/components/Button';
import Modal from '~/components/Modal';
import { AllowAccessStep } from '~/components/oauth/Steps';
import { api } from '~/utils/api';
import { EcommsIntegrationProvider } from '@prisma/client';

import globalRelayLogo from '../../../../../../../public/global relay.png';
import smarshLogo from '../../../../../../../public/smarsh.png';

export const getProviderDisplayName = (provider: EcommsIntegrationProvider): string => {
  switch (provider) {
    case EcommsIntegrationProvider.GLOBAL_RELAY_9:
      return 'Global Relay 9';
    case EcommsIntegrationProvider.GLOBAL_RELAY_10:
      return 'Global Relay 10';
    case EcommsIntegrationProvider.SMARSH:
      return 'Smarsh';
    default:
      return provider;
  }
};

export enum CommunicationOAuthSteps {
  ALLOW_ACCESS = 1,
  BEFORE_YOU_PROCEED = 2,
  SETTINGS = 3,
}

export type EcommsIntegrationProviderProps = {
  provider: EcommsIntegrationProvider | null;
  modalRef: React.RefObject<HTMLDialogElement | null>;
  closeModal: () => void;
  initialStep: CommunicationOAuthSteps;
  existingIntegration?: any;
};

const globalRelay = [
  {
    title: 'Email Communications',
    points: [
      'Sender information',
      'Recipients (To, CC, BCC)',
      'Subject lines',
      'Email body content',
      'Attachments',
      'Timestamps',
      'Message IDs',
      'Threading information',
    ],
  },
  {
    title: 'Chat Messages',
    points: [
      'Participants',
      'Message content',
      'Timestamps',
      'Channel information',
      'Thread context',
      'File attachments',
    ],
  },
  {
    title: 'Voice Communications',
    points: [
      'Call participants',
      'Call duration',
      'Timestamps',
      'Call records',
      'Recording metadata',
    ],
  },
  {
    title: 'User Information',
    points: ['User names', 'Email addresses', 'Department information', 'Role information'],
  },
];

const permissions = {
  [EcommsIntegrationProvider.GLOBAL_RELAY_9]: globalRelay,
  [EcommsIntegrationProvider.GLOBAL_RELAY_10]: globalRelay,
  [EcommsIntegrationProvider.SMARSH]: [
    {
      title: 'Email Communications',
      points: [
        'Sender information',
        'Recipients (To, CC, BCC)',
        'Subject lines',
        'Email body content',
        'Attachments',
        'Timestamps',
        'Message IDs',
        'Delivery status',
      ],
    },
    {
      title: 'Instant Messages',
      points: [
        'Participants',
        'Message content',
        'Timestamps',
        'Platform information',
        'Status information',
        'File transfers',
      ],
    },
    {
      title: 'Social Media Posts',
      points: [
        'Post content',
        'Author information',
        'Timestamps',
        'Platform details',
        'Engagement metrics',
        'Comments and replies',
      ],
    },
    {
      title: 'Mobile Communications',
      points: [
        'SMS/MMS content',
        'Phone numbers',
        'Timestamps',
        'Delivery status',
        'Media attachments',
      ],
    },
    {
      title: 'User Information',
      points: ['User names', 'Contact information', 'Organization details', 'Access permissions'],
    },
  ],
};

type CredentialFormProps = {
  provider: EcommsIntegrationProvider;
  closeModal: () => void;
};

const CredentialForm = ({ provider, closeModal }: CredentialFormProps) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [isConnecting, setIsConnecting] = useState(false);

  const saveIntegrationMutation = api.ecomms.upsertEcommsIntegration.useMutation({
    onSuccess: (result) => {
      setIsConnecting(false);
      closeModal();
      toast.success(`Successfully connected to ${getProviderDisplayName(provider)}`);
    },
    onError: (error) => {
      console.error('Failed to save integration:', error);
      setIsConnecting(false);
      toast.error(`Failed to connect to ${getProviderDisplayName(provider)}`);
    },
  });

  const handleSubmit = async () => {
    if (!username.trim() || !password.trim()) {
      return;
    }

    setIsConnecting(true);

    // Use the actual API call without simulation delay
    saveIntegrationMutation.mutate({
      provider,
      username: username.trim(),
      password: password.trim(),
    });
  };

  return (
    <div className="space-y-4 pt-4">
      <div>
        <label className="text-foreground mb-2 block text-sm font-medium">Username</label>
        <input
          type="text"
          className="border-input bg-background focus-visible:ring-ring/50 h-9 w-full rounded-md border px-3 text-sm outline-none focus-visible:ring-[3px]"
          placeholder="Enter bot username"
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          disabled={isConnecting}
        />
      </div>
      <div>
        <label className="text-foreground mb-2 block text-sm font-medium">Password</label>
        <input
          type="password"
          className="border-input bg-background focus-visible:ring-ring/50 h-9 w-full rounded-md border px-3 text-sm outline-none focus-visible:ring-[3px]"
          placeholder="Enter bot password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          disabled={isConnecting}
        />
      </div>
      <div className="pt-2">
        <Button
          isLoading={isConnecting}
          label={
            isConnecting
              ? `Connecting to ${getProviderDisplayName(provider)}...`
              : `Connect to ${getProviderDisplayName(provider)}`
          }
          className="bg-black text-xl text-[#FFFFFF] w-full"
          onClick={handleSubmit}
          disabled={!username.trim() || !password.trim() || isConnecting}
        />
      </div>
    </div>
  );
};

type BeforeYouProceedProps = Pick<EcommsIntegrationProviderProps, 'provider'> & {
  setExternalPopup: React.Dispatch<React.SetStateAction<Window | null>>;
  closeModal: () => void;
};

const BeforeYouProceed = ({ provider, setExternalPopup, closeModal }: BeforeYouProceedProps) => {
  const [showCredentialForm, setShowCredentialForm] = useState(false);

  const handleAcknowledge = () => {
    setShowCredentialForm(true);
  };

  if (!provider) return null;

  return (
    <>
      <h3 className="font-semibold text-2xl">Before You Proceed...</h3>
      <p className="py-1.5 text-[#909799]">
        Please set up a bot account with read-only permissions to all messaging inboxes for{' '}
        {getProviderDisplayName(provider)}.
      </p>
      {!showCredentialForm ? (
        <div className="pt-5">
          <Button
            label="Acknowledge"
            className="bg-black text-xl text-[#FFFFFF] w-full"
            onClick={handleAcknowledge}
          />
        </div>
      ) : (
        <CredentialForm provider={provider} closeModal={closeModal} />
      )}
    </>
  );
};

type IntegrationSettingsProps = {
  provider: EcommsIntegrationProvider;
  closeModal: () => void;
  existingIntegration?: any;
};

const IntegrationSettings = ({
  provider,
  closeModal,
  existingIntegration,
}: IntegrationSettingsProps) => {
  const [username, setUsername] = useState(existingIntegration?.username || '');
  const [password, setPassword] = useState('');
  const [syncFrequency, setSyncFrequency] = useState('24h');
  const [isUpdating, setIsUpdating] = useState(false);
  const [isRemoving, setIsRemoving] = useState(false);

  // Track if any changes have been made
  const [hasChanges, setHasChanges] = useState(false);

  // Track initial values to detect changes
  const initialUsername = existingIntegration?.username || '';
  const initialSyncFrequency = '24h';

  // Check if values have changed
  useEffect(() => {
    const usernameChanged = username !== initialUsername;
    const passwordChanged = password !== '';
    const syncFrequencyChanged = syncFrequency !== initialSyncFrequency;

    setHasChanges(usernameChanged || passwordChanged || syncFrequencyChanged);
  }, [username, password, syncFrequency, initialUsername, initialSyncFrequency]);

  const updateIntegrationMutation = api.ecomms.upsertEcommsIntegration.useMutation({
    onSuccess: () => {
      setIsUpdating(false);
      closeModal();
      toast.success(`Successfully updated ${getProviderDisplayName(provider)} integration`);
    },
    onError: (error) => {
      console.error('Failed to update integration:', error);
      setIsUpdating(false);
      toast.error(`Failed to update ${getProviderDisplayName(provider)} integration`);
    },
  });

  const handleUpdate = async () => {
    if (!hasChanges) {
      closeModal();
      return;
    }

    if (!username.trim() || (!existingIntegration && !password.trim())) {
      return;
    }

    setIsUpdating(true);

    // For demo purposes, just close the modal after a brief delay
    setTimeout(() => {
      setIsUpdating(false);
      closeModal();
    }, 500);
  };

  const handleRemove = async () => {
    setIsRemoving(true);
    // TODO: Implement remove integration API call
    setTimeout(() => {
      setIsRemoving(false);
      closeModal();
      toast.success(`Successfully removed ${getProviderDisplayName(provider)} integration`);
    }, 2000);
  };

  const syncFrequencyOptions = [
    { value: '24h', label: 'Every 24 hours' },
    { value: '48h', label: 'Every other day' },
    { value: '168h', label: 'Every week' },
  ];

  return (
    <>
      <h3 className="font-semibold text-2xl">{getProviderDisplayName(provider)} Settings</h3>
      <p className="py-1.5 text-[#909799]">
        Manage your {getProviderDisplayName(provider)} integration settings.
      </p>

      <div className="space-y-4 pt-4">
        <div>
          <label className="text-foreground mb-2 block text-sm font-medium">Username</label>
          <input
            type="text"
            className="border-input bg-background focus-visible:ring-ring/50 h-9 w-full rounded-md border px-3 text-sm outline-none focus-visible:ring-[3px]"
            placeholder={existingIntegration ? '••••••••' : 'Enter bot username'}
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            disabled={isUpdating || isRemoving}
          />
        </div>

        <div>
          <label className="text-foreground mb-2 block text-sm font-medium">Password</label>
          <input
            type="password"
            className="border-input bg-background focus-visible:ring-ring/50 h-9 w-full rounded-md border px-3 text-sm outline-none focus-visible:ring-[3px]"
            placeholder={existingIntegration ? '••••••••' : 'Enter password'}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            disabled={isUpdating || isRemoving}
          />
        </div>

        <div>
          <label className="text-foreground mb-2 block text-sm font-medium">Sync Frequency</label>
          <select
            className="border-input bg-background focus-visible:ring-ring/50 h-9 w-full rounded-md border px-3 text-sm outline-none focus-visible:ring-[3px]"
            value={syncFrequency}
            onChange={(e) => setSyncFrequency(e.target.value)}
            disabled={isUpdating || isRemoving}
          >
            {syncFrequencyOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        <div className="pt-2 space-y-3">
          <Button
            isLoading={isUpdating}
            label={isUpdating ? 'Updating...' : 'Update settings'}
            className={`text-xl text-[#FFFFFF] w-full ${
              hasChanges ? 'bg-black hover:bg-gray-800' : 'bg-gray-400 cursor-not-allowed'
            }`}
            onClick={handleUpdate}
            disabled={!hasChanges || isUpdating || isRemoving}
          />

          <Button
            isLoading={isRemoving}
            label={isRemoving ? 'Removing...' : 'Remove integration'}
            className="bg-red-600 text-xl text-[#FFFFFF] w-full hover:bg-red-700"
            onClick={handleRemove}
            disabled={isUpdating || isRemoving}
          />
        </div>
      </div>
    </>
  );
};

export default function CommunicationPlatformModal({
  provider,
  modalRef,
  closeModal,
  initialStep,
  existingIntegration,
}: EcommsIntegrationProviderProps) {
  const [step, setStep] = useState<CommunicationOAuthSteps>(initialStep);
  const [externalPopup, setExternalPopup] = useState<Window | null>(null);

  useEffect(() => {
    setStep(initialStep);
  }, [initialStep]);

  // Mock external popup behavior for now
  // useExternalPopup(externalPopup, setExternalPopup, provider!, () => {
  //   closeModal();
  // });

  // Simulate popup completion for demo purposes
  React.useEffect(() => {
    if (externalPopup) {
      const timer = setTimeout(() => {
        setExternalPopup(null);
        closeModal();
      }, 3000); // Simulate 3 second OAuth process

      return () => clearTimeout(timer);
    }
  }, [externalPopup, closeModal]);

  const getImageSrc = () => {
    if (provider === EcommsIntegrationProvider.GLOBAL_RELAY_10) {
      return globalRelayLogo.src;
    }
    if (provider === EcommsIntegrationProvider.GLOBAL_RELAY_9) {
      return globalRelayLogo.src;
    }
    if (provider === EcommsIntegrationProvider.SMARSH) {
      return smarshLogo.src;
    }
    return '';
  };

  const getComponentAsPerStep = () => {
    const components: Record<CommunicationOAuthSteps, React.ReactElement<any>> = {
      [CommunicationOAuthSteps.ALLOW_ACCESS]: (
        <AllowAccessStep<CommunicationOAuthSteps>
          provider={provider!}
          permissions={permissions}
          setStep={setStep}
          nextStep={CommunicationOAuthSteps.BEFORE_YOU_PROCEED}
        />
      ),
      [CommunicationOAuthSteps.BEFORE_YOU_PROCEED]: (
        <BeforeYouProceed
          setExternalPopup={setExternalPopup}
          provider={provider}
          closeModal={closeModal}
        />
      ),
      [CommunicationOAuthSteps.SETTINGS]: (
        <IntegrationSettings
          provider={provider!}
          closeModal={closeModal}
          existingIntegration={existingIntegration}
        />
      ),
    };
    return components[step];
  };

  return (
    <Modal
      ref={modalRef}
      open={!!provider}
     
      header=""
      onClose={() => setTimeout(() => setStep(initialStep), 1000)}
    >
      {step !== CommunicationOAuthSteps.SETTINGS && (
        <div className="flex">
          <div className="m-auto p-8">
            <Image src={getImageSrc()} alt="logo" width={130} height={120} />
          </div>
        </div>
      )}
      {getComponentAsPerStep()}
    </Modal>
  );
}
